import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { TResponseTimeByDay } from "@/types/TAnalytics";

type ResponseTimeChartProps = {
  brokerData: TResponseTimeByDay[];
  claimData: TResponseTimeByDay[];
};

export function ResponseTimeChart({ brokerData, claimData }: ResponseTimeChartProps) {
  const { t } = useTranslation();

  const allDates = new Set([
    ...brokerData.map(d => d.date),
    ...claimData.map(d => d.date),
  ]);

  const chartData = Array.from(allDates)
    .sort()
    .map(date => {
      const broker = brokerData.find(d => d.date === date);
      const claim = claimData.find(d => d.date === date);
      return {
        date: new Date(date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }),
        broker: broker?.avg_ms ?? null,
        claim: claim?.avg_ms ?? null,
      };
    });

  if (chartData.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          {t("analytics.responseTime.title", "Antwortzeit-Verlauf")}
        </h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          {t("analytics.noData", "Keine Daten verfügbar")}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        {t("analytics.responseTime.title", "Antwortzeit-Verlauf")}
      </h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis
              tick={{ fontSize: 12 }}
              tickFormatter={(v) => `${v}ms`}
            />
            <Tooltip
              formatter={(value) => [`${value}ms`, ""]}
              contentStyle={{
                backgroundColor: "var(--tooltip-bg, #fff)",
                border: "1px solid var(--tooltip-border, #e5e7eb)",
                borderRadius: "0.5rem",
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="broker"
              name={t("analytics.responseTime.broker", "Berater")}
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: "#3b82f6", r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="claim"
              name={t("analytics.responseTime.claim", "Schadenstatus")}
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: "#10b981", r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
